import {useMessage} from '@coolcolduk/ui-mui';
import {ContentCopy, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {ExamModule} from '@cp/model';
import BondModuleTopicDropdown from '../../bonds/bondModuleTopicDropdown/BondModuleTopicDropdown';
import {useGenerateStudyPrompts} from '../../hooks/generate/useGenerateStudyPrompts';

export interface GenerateStudyPromptDialogProps {
  open: boolean;
  examModules: ExamModule[];
  modulesLoading: boolean;
  initialModuleId?: string;
  initialTopic?: string;
  initialAdditionalInfo?: string;
  onClose: () => void;
  onApply: (studyContent: string, moduleId: string, topic: string, additionalInfo?: string) => void;
}

export default function GenerateStudyPromptDialog({
  open,
  examModules,
  modulesLoading,
  initialModuleId,
  initialTopic,
  initialAdditionalInfo,
  onClose,
  onApply,
}: GenerateStudyPromptDialogProps) {
  const {showMessage} = useMessage();
  const [moduleId, setModuleId] = useState<string | undefined>(initialModuleId);
  const [topic, setTopic] = useState<string | undefined>(initialTopic);
  const [additionalInfo, setAdditionalInfo] = useState(initialAdditionalInfo || '');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [aiOutput, setAiOutput] = useState('');

  const {generateStudyPrompts, loading} = useGenerateStudyPrompts();

  const currentExamModule = useMemo(() => {
    return examModules.find((m) => m.id === moduleId);
  }, [moduleId, examModules]);

  const currentModuleTopic = useMemo(() => {
    return currentExamModule?.topics.find((t) => t.topic === topic);
  }, [topic, currentExamModule]);

  // Update state when initial values change (when dialog opens with new values)
  useEffect(() => {
    if (open) {
      setModuleId(initialModuleId);
      setTopic(initialTopic);
      setAdditionalInfo(initialAdditionalInfo || '');
      setSystemPrompt('');
      setUserPrompt('');
      setAiOutput('');
    }
  }, [open, initialModuleId, initialTopic, initialAdditionalInfo]);

  const handleModuleChange = useCallback(
    (newModuleId?: string) => {
      setModuleId(newModuleId);
      setTopic(undefined); // Reset topic when module changes
    },
    [setModuleId, setTopic],
  );

  const handleCreatePrompt = useCallback(async () => {
    if (!moduleId || !topic) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    try {
      const prompts = await generateStudyPrompts({
        moduleId,
        topic,
        additionalInfo: additionalInfo.trim() || undefined,
      });

      setSystemPrompt(prompts.system);
      setUserPrompt(prompts.user);
    } catch (error) {
      console.error('Error generating prompts:', error);
      showMessage('Failed to generate prompts. Please try again.', 6000, 'error');
    }
  }, [moduleId, topic, additionalInfo, generateStudyPrompts, showMessage]);

  const handleCopySystemPrompt = useCallback(() => {
    navigator.clipboard.writeText(systemPrompt);
    showMessage('System prompt copied to clipboard', 3000, 'success');
  }, [systemPrompt, showMessage]);

  const handleCopyUserPrompt = useCallback(() => {
    navigator.clipboard.writeText(userPrompt);
    showMessage('User prompt copied to clipboard', 3000, 'success');
  }, [userPrompt, showMessage]);

  const handleAiOutputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAiOutput(e.target.value);
  }, []);

  const handleApply = useCallback(() => {
    if (!aiOutput.trim()) {
      showMessage('Please paste the AI-generated study content', 6000, 'error');
      return;
    }

    if (!moduleId || !topic) {
      showMessage('Please ensure module and topic are selected', 6000, 'error');
      return;
    }

    onApply(aiOutput.trim(), moduleId, topic, additionalInfo.trim() || undefined);
    showMessage('Study content applied successfully!', 6000, 'success');
    onClose();
  }, [aiOutput, moduleId, topic, additionalInfo, onApply, onClose, showMessage]);

  const handleClose = useCallback(() => {
    setSystemPrompt('');
    setUserPrompt('');
    setAiOutput('');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>Generate Study Prompts</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{pt: 2}}>
          <BondModuleTopicDropdown
            moduleId={moduleId}
            topic={topic}
            examModules={examModules}
            modulesLoading={modulesLoading}
            hideTopicWhenNoModule
            onModuleChange={handleModuleChange}
            onTopicChange={setTopic}
          />

          <TextField
            label="Module details"
            value={`${currentModuleTopic?.detail}`}
            disabled
            fullWidth
            multiline
            rows={3}
            InputProps={{readOnly: true}}
            sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional Information (Optional)"
            placeholder="Add any specific requirements, focus areas, or additional context for the study guide..."
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            helperText="Specify what should be included, emphasized, or any other requirements for the generated study guide"
          />

          <Button
            variant="contained"
            onClick={handleCreatePrompt}
            disabled={!moduleId || !topic || loading}
            sx={{
              bgcolor: '#FF6F59',
              '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Prompt'}
          </Button>

          {userPrompt && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  User Prompt
                </Typography>
                <Tooltip title="Copy user prompt">
                  <IconButton
                    component="div"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyUserPrompt();
                    }}
                    size="small"
                    sx={{ml: 1}}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={userPrompt}
                  InputProps={{readOnly: true}}
                  sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {systemPrompt && (
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  },
                }}
              >
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  System Prompt
                </Typography>
                <Tooltip title="Copy system prompt">
                  <IconButton
                    component="div"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopySystemPrompt();
                    }}
                    size="small"
                    sx={{ml: 1}}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={systemPrompt}
                  InputProps={{readOnly: true}}
                  sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
                />
              </AccordionDetails>
            </Accordion>
          )}

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Typography variant="h6">Paste AI-Generated Study Content</Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={aiOutput}
              onChange={handleAiOutputChange}
              placeholder="Paste the AI-generated study content here..."
              sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
            />
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!aiOutput.trim()}
              sx={{
                bgcolor: '#FF6F59',
                '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
                alignSelf: 'flex-start',
              }}
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
